import React, { useEffect, useState } from 'react';
import { Menu } from '../../types';
import {
  Button,
  Divider,
  Form,
  Input,
  Pagination,
  Select,
  SelectProps,
  Table,
  TableColumnsType,
  Tag,
} from 'antd';
import Wrapper from '~/App/components/Wrapper';
import { useLoadingSetter } from '~/providers/LoadingProvider';
import { SearchMenuRequest, searchMenus } from '../../api';
import { formatPrice } from '~/utils/currency';
import { Category } from '~/App/shared/types/Category';
import { getCategories } from '~/App/shared/api';
import message from '~/utils/message';
import { useNavigate } from 'react-router-dom';

const ListMenu = () => {
  const setLoading = useLoadingSetter();
  const [menus, setMenus] = useState<Menu[]>([]);
  const [totalElements, setTotalElements] = useState<number>(0);
  const [categoryOptions, setCategoryOptions] = useState<
    SelectProps['options']
  >([]);
  const [searchReq, setSearchReq] = useState<SearchMenuRequest>({
    page: 1,
    size: 10,
    sort: [
      {
        field: 'seq',
        order: 'asc',
      },
    ],
  });
  const [form] = Form.useForm();
  const nav = useNavigate();

  // Get all categories for one time.
  useEffect(() => {
    getAllCategories();
  }, []);

  // Refresh menus when searchReq changes.
  useEffect(() => {
    getMenus();
  }, [searchReq]);

  const getMenus = async () => {
    setLoading(true);
    // Hit API.
    try {
      const { data, errorMsg } = await searchMenus(searchReq);
      setMenus(data.menus);
      setTotalElements(data.totalElements);
    } catch (error: any) {
      message.error('Failed to fetch menus: ' + error);
    } finally {
      setLoading(false);
    }
  };

  const getAllCategories = async () => {
    setLoading(true);
    // Hit API.
    try {
      const { data } = await getCategories();
      const options = (data ?? []).map((category: Category) => ({
        label: category.name,
        value: category.id,
      }));
      setCategoryOptions(options);
    } catch (error: any) {
      message.error('Failed to fetch categories: ' + error);
    } finally {
      setLoading(false);
    }
  };

  const handleFinish = async (values: any) => {
    const { menuKeyword, categories } = values;
    setSearchReq((prev) => {
      return {
        ...prev,
        page: 1,
        filters: {
          menuKeyword,
          categoryIds: categories,
        },
      };
    });
  };

  const handleChangePage = async (page: number, pageSize: number) => {
    setSearchReq((prev) => {
      return {
        ...prev,
        page,
        size: pageSize,
      };
    });
  };

  const handleEdit = (menuId: string) => {
    nav(`${menuId}`);
  };

  const handleClickNewMenu = () => {
    nav('new');
  };

  const columns: TableColumnsType<Menu> = [
    {
      title: 'Category',
      dataIndex: 'categoryName',
    },
    {
      title: 'Name',
      dataIndex: 'name',
    },
    {
      title: 'Price',
      dataIndex: 'price',
      render: (_, { price }) => {
        return formatPrice(price);
      },
    },
    {
      title: 'Sequence',
      dataIndex: 'seq',
    },
    {
      title: 'Active',
      dataIndex: 'isActive',
      render: (_, { isActive }) => {
        if (isActive) {
          return <Tag color="green">ACTIVE</Tag>;
        }
        return <Tag color="red">INACTIVE</Tag>;
      },
    },
    {
      title: 'Action',
      key: 'action',
      render: (_, record) => (
        <Button type="link" onClick={() => handleEdit(record.id)}>
          Edit
        </Button>
      ),
    },
  ];

  return (
    <Wrapper>
      <Form form={form} layout="inline" onFinish={handleFinish}>
        <Form.Item name="categories" label="Categories">
          <Select
            mode="multiple"
            allowClear
            placeholder="Select categories"
            options={categoryOptions}
            style={{ width: 250 }}
          />
        </Form.Item>
        <Form.Item name="menuKeyword" label="Menu Name">
          <Input />
        </Form.Item>
        <Form.Item>
          <Button type="primary" htmlType="submit">
            Search
          </Button>
        </Form.Item>
      </Form>
      <Divider />
      <div className="flex justify-between">
        <Button type="primary" onClick={handleClickNewMenu}>
          New Menu
        </Button>
        <Pagination
          current={searchReq.page}
          pageSize={searchReq.size}
          total={totalElements}
          style={{ textAlign: 'right' }}
          onChange={handleChangePage}
          showSizeChanger
          pageSizeOptions={[10, 20, 50]}
        />
      </div>
      <br />
      <Table columns={columns} dataSource={menus} pagination={false} />
    </Wrapper>
  );
};

export default ListMenu;
